import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Package, Clock, Fuel, Route, Gauge,
  PlaneTakeoff, Timer, Radio, ChevronDown, ArrowUpRight,
} from "lucide-react";
import { getStats, getLiveFlights } from "@/lib/api";

const GROUP_ICONS = {
  users: Users,
  package: Package,
  clock: Clock,
  fuel: Fuel,
  route: Route,
  gauge: Gauge,
};

const FEATURED_ICONS = {
  alltime_flights: PlaneTakeoff,
  alltime_hours: Timer,
  airborne: Radio,
};

const FeaturedCard = ({ stat, index, liveValue }) => {
  const Icon = FEATURED_ICONS[stat.key] || PlaneTakeoff;
  const value = liveValue != null ? liveValue : stat.value;
  const cardCls =
    "relative block h-full overflow-hidden rounded-2xl border border-white/5 bg-[var(--aiv-surface)] p-7 shadow-[0_0_40px_rgba(138,21,56,0.12)]";

  const inner = (
    <>
      <div className="absolute inset-0 bg-gradient-to-br from-[#8A1538]/20 to-transparent" />
      <div className="relative flex items-center justify-between">
        <p className="text-overline flex items-center gap-2 text-[var(--aiv-text-2)]">
          {stat.live && (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
          )}
          {stat.label}
        </p>
        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--aiv-accent)]/25 bg-[var(--aiv-accent)]/5">
          <Icon className="h-4 w-4 text-[var(--aiv-accent)]" />
        </span>
      </div>
      <p className="relative mt-6 whitespace-nowrap font-mono-data text-4xl font-bold tracking-tight text-[var(--aiv-text)] lg:text-5xl">
        <CountUp end={value} duration={2.2} separator="," enableScrollSpy scrollSpyOnce preserveValue />
        <span className="text-[var(--aiv-accent)]">{stat.suffix}</span>
      </p>
      {stat.live && (
        <span className="relative mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--aiv-accent)]">
          View the live map
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      )}
    </>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      data-testid={`stat-featured-${stat.key}`}
    >
      {stat.live ? (
        <Link
          to="/live-flights"
          data-testid="stat-airborne-link"
          className={`group ${cardCls} transition-colors duration-200 hover:border-[var(--aiv-accent)]/40`}
        >
          {inner}
        </Link>
      ) : (
        <div className={cardCls}>{inner}</div>
      )}
    </motion.div>
  );
};

const GroupCard = ({ group, index }) => {
  const [open, setOpen] = useState(false);
  const Icon = GROUP_ICONS[group.icon] || Gauge;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      data-testid={`stat-group-${group.key}`}
      className="overflow-hidden rounded-2xl border border-white/5 bg-[var(--aiv-surface)] transition-colors duration-300 hover:border-[var(--aiv-accent)]/25"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`stat-group-toggle-${group.key}`}
        className="flex w-full items-center gap-4 p-6 text-left"
        aria-expanded={open}
      >
        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-[var(--aiv-accent)]/25 bg-[var(--aiv-accent)]/5">
          <Icon className="h-5 w-5 text-[var(--aiv-accent)]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-overline text-[var(--aiv-text-2)]">{group.label}</p>
          <p className="mt-1 whitespace-nowrap font-mono-data text-xl font-bold text-[var(--aiv-text)]">
            {group.total}
          </p>
        </div>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 text-[var(--aiv-text-2)] transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-2 border-t border-white/5 px-6 py-4" data-testid={`stat-group-details-${group.key}`}>
              {group.details.map(([label, val]) => (
                <div key={label} className="flex items-center justify-between gap-4">
                  <span className="text-sm text-[var(--aiv-text-2)]">{label}</span>
                  <span className="whitespace-nowrap font-mono-data text-sm font-semibold text-[var(--aiv-accent)]">{val}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const Statistics = () => {
  const [featured, setFeatured] = useState([]);
  const [groups, setGroups] = useState([]);
  const [airborne, setAirborne] = useState(null);

  useEffect(() => {
    getStats()
      .then((d) => {
        setFeatured(d.featured || []);
        setGroups(d.groups || []);
      })
      .catch(() => {});
  }, []);

  // Keep the "In The Air Now" count fresh (re-animates on change).
  useEffect(() => {
    let active = true;
    const tick = () =>
      getLiveFlights()
        .then((d) => active && setAirborne(d.count))
        .catch(() => {});
    tick();
    const t = setInterval(tick, 30000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, []);

  return (
    <section id="stats" data-testid="stats-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="mb-14 max-w-2xl">
          <p className="text-overline text-[#8A1538]">By the numbers</p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[var(--aiv-text)] lg:text-4xl [text-wrap:balance]">
            15 years logged in the virtual skies
          </h2>
          <p className="mt-4 text-base text-[var(--aiv-text-2)]">
            Every flight is a filed PIREP. Explore our headline milestones — and
            drill into the detail behind each metric.
          </p>
        </div>

        {/* Featured — always visible, no drill-down */}
        <div className="grid gap-5 sm:grid-cols-3">
          {featured.map((s, i) => (
            <FeaturedCard key={s.key} stat={s} index={i} liveValue={s.live ? airborne : null} />
          ))}
        </div>

        {/* Drill-down group cards */}
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g, i) => (
            <GroupCard key={g.key} group={g} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
